import React from 'react';
import { 
  DollarSign, Shield, Zap, Mail, ChevronRight, FileText, 
  Crown, CreditCard, ShieldCheck, Sparkles, Receipt, 
  ArrowUpRight, Landmark, Lock, CheckCircle2 
} from 'lucide-react';
import { motion } from 'framer-motion';

// UI Components
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import HeaderActions from '../components/HeaderActions';

// Contexts
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext'; // 🔥 Dil Entegrasyonu

const FeatureItem = ({ icon: Icon, title, description, color }) => (
    <motion.div 
      whileHover={{ y: -5 }}
      className="flex items-start space-x-5 p-6 rounded-[2rem] bg-white dark:bg-[#111827] border border-slate-100 dark:border-white/5 shadow-xl shadow-slate-200/50 dark:shadow-none ring-1 ring-slate-200/50 dark:ring-white/5 group transition-all duration-300 hover:border-blue-500/20"
    >
        <div className={`p-3 rounded-2xl bg-${color}-500/10 text-${color}-500 shrink-0 group-hover:scale-110 transition-transform duration-300`}>
          <Icon size={22} />
        </div>
        <div>
            <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-tight text-sm mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{title}</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">{description}</p>
        </div>
    </motion.div>
);

const SubscriptionPage = () => {
    const { user } = useAuth();
    
    // 🔥 Dil Fonksiyonunu Al
    const { t } = useLanguage(); 

    const plan = user?.subscriptionPlan || 'free';
    const isPremium = plan !== 'free';

    // Gerçek Stripe Linki
    const handlePortalRedirect = () => {
        const portalUrl = "https://billing.stripe.com/p/login/YOUR_PUBLIC_KEY"; 
        window.open(portalUrl, '_blank');
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#070b14] transition-colors duration-500 font-sans pb-32">
            
            {/* Background Decor */}
            <div className="fixed top-0 left-0 w-full h-[600px] bg-gradient-to-b from-blue-50/50 to-transparent dark:from-blue-900/10 dark:to-transparent pointer-events-none" />

            {/* HEADER */}
            <header className="sticky top-0 z-50 bg-[#f8fafc]/80 dark:bg-[#070b14]/80 backdrop-blur-xl border-b border-slate-200 dark:border-white/5 px-6 py-4 transition-all duration-300">
                <div className="max-w-5xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Badge className="bg-blue-600/10 text-blue-600 dark:text-blue-400 border-0 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-sm">
                           {t('billing_center') || 'Billing Center'}
                        </Badge>
                    </div>
                    <HeaderActions />
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 pt-12 relative z-10">
                
                {/* PAGE HERO */}
                <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-16">
                    <div className="space-y-3 max-w-xl">
                        <h1 className="text-5xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-[0.9]">
                            {t('sub_hero_title') || 'Upgrade to'} <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-purple-600">{t('sub_hero_highlight') || 'Elite Status.'}</span>
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 font-medium text-lg leading-relaxed">{t('sub_hero_subtitle') || 'Unlock unlimited AI power and audit protection.'}</p>
                    </div>
                    <div className="flex items-center gap-3 px-5 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full backdrop-blur-sm">
                        <ShieldCheck size={18} className="text-emerald-500" />
                        <span className="text-[10px] font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">{t('secured_by_stripe') || 'Secured by Stripe'}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* LEFT: MASTER MEMBERSHIP CARD */}
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="lg:col-span-8"
                    >
                        <Card className={`relative overflow-hidden border-0 shadow-2xl rounded-[3rem] h-full transition-all duration-500 group ${isPremium ? 'bg-gradient-to-br from-[#003366] to-[#001a33] text-white' : 'bg-white dark:bg-[#111827] text-slate-900 dark:text-white ring-1 ring-slate-200 dark:ring-white/5 hover:shadow-orange-500/10'}`}>
                            {/* Card Texture */}
                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] mix-blend-overlay"></div>
                            
                            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                                {isPremium ? <Crown size={220} /> : <Zap size={220} />}
                            </div>
                            
                            <CardHeader className="p-10 pb-0 relative z-10">
                                <div className="flex justify-between items-start">
                                    <div className="space-y-4">
                                        <Badge className={`${isPremium ? 'bg-orange-600 text-white' : 'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-400'} border-0 px-5 py-2 text-[10px] font-black uppercase tracking-[0.2em] shadow-lg`}>
                                            {isPremium ? (t('pro_membership') || 'Pro Membership') : (t('standard_plan') || 'Standard Plan')}
                                        </Badge>
                                        <h3 className={`text-4xl font-black tracking-tighter uppercase ${isPremium ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                                            {isPremium ? (t('premium_plan') || 'PREMIUM PLAN') : (t('plan_free_title') || 'FREE PLAN')}
                                        </h3>
                                    </div>
                                    <div className={`p-4 rounded-3xl shadow-lg ${isPremium ? 'bg-white/10 backdrop-blur-md' : 'bg-slate-5 dark:bg-white/5'}`}>
                                        <Sparkles size={32} className={isPremium ? "text-orange-400 animate-pulse" : "text-slate-400"} />
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="p-10 space-y-10 relative z-10">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 py-8 border-y border-dashed border-slate-200 dark:border-white/10">
                                    <div>
                                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isPremium ? 'text-blue-300' : 'text-slate-400'}`}>{t('account_status') || 'Account Status'}</p>
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2.5 h-2.5 rounded-full animate-pulse shadow-[0_0_10px] ${isPremium ? 'bg-emerald-400 shadow-emerald-400' : 'bg-orange-400 shadow-orange-400'}`} />
                                            <span className="text-xl font-black uppercase italic tracking-tighter">{isPremium ? (t('active_verified') || "Active & Verified") : (t('free_access') || "Free Access")}</span>
                                        </div>
                                    </div>
                                    {isPremium && (
                                        <div className="bg-white/5 backdrop-blur-md p-4 rounded-2xl border border-white/10 flex items-center gap-4 shadow-inner">
                                            <div className="p-2.5 bg-blue-500/20 rounded-xl text-blue-300"><CreditCard size={20}/></div>
                                            <div>
                                                <p className="text-[9px] font-black text-blue-200 uppercase tracking-widest leading-none">{t('payment_method') || 'Payment Method'}</p>
                                                <p className="font-mono text-sm font-bold pt-1 opacity-90">•••• 4242</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <Button 
                                    onClick={handlePortalRedirect}
                                    className={`w-full h-16 rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl hover:shadow-2xl transition-all active:scale-[0.98] group ${isPremium ? 'bg-white text-slate-900 hover:bg-blue-50' : 'bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:from-orange-500 hover:to-orange-400'}`}
                                >
                                    {isPremium ? (t('manage_billing') || 'Manage Billing Terminal') : (t('upgrade_pro') || 'Upgrade to Pro Experience')}
                                    <ChevronRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* RIGHT: INVOICE HISTORY QUICK ACCESS */}
                    <motion.div 
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="lg:col-span-4 space-y-6"
                    >
                        <Card className="bg-white dark:bg-[#111827] border-0 shadow-xl rounded-[2.5rem] p-8 ring-1 ring-slate-200 dark:ring-white/5 h-full flex flex-col justify-between hover:shadow-2xl transition-shadow duration-300">
                            <div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3.5 bg-blue-600/10 rounded-2xl text-blue-600 dark:text-blue-400"><Receipt size={24} /></div>
                                    <h4 className="font-black text-sm uppercase tracking-tighter italic text-slate-900 dark:text-white">{t('invoice_history') || 'Invoice History'}</h4>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-10">
                                    {t('invoice_history_desc') || 'Access all past e-invoices and payment details for your BüroFuchs subscription securely here.'}
                                </p>
                            </div>
                            <Button variant="ghost" className="w-full h-14 rounded-2xl bg-slate-5 dark:bg-white/5 border border-slate-200 dark:border-white/10 font-bold text-[10px] uppercase tracking-widest text-slate-600 dark:text-slate-300 hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 transition-all">
                                <FileText size={16} className="mr-2" /> {t('list_invoices') || 'List Invoices'}
                            </Button>
                        </Card>
                    </motion.div>
                </div>

                {/* PREMIUM BENEFITS SECTION */}
                <div className="mt-24 space-y-12">
                    <div className="flex items-center gap-6">
                        <div className="h-px bg-slate-200 dark:bg-white/10 flex-1"></div>
                        <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] bg-slate-50 dark:bg-[#070b14] px-4 py-1 rounded-full border border-slate-200 dark:border-white/5">
                            {t('premium_benefits_title') || 'Premium Benefits'}
                        </h2>
                        <div className="h-px bg-slate-200 dark:bg-white/10 flex-1"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FeatureItem icon={Zap} title={t('feat_unlimited') || 'Unlimited AI'} description={t('feat_desc_unlimited') || "Unlimited document analysis and secure cloud storage."} color="orange" />
                        <FeatureItem icon={Shield} title={t('feat_risk_radar') || 'Risk Radar 24/7'} description={t('feat_desc_risk') || "AI monitoring for deadlines and financial risks."} color="rose" />
                        <FeatureItem icon={Mail} title={t('feat_templates') || 'Smart Templates'} description={t('feat_desc_templates') || "Professional templates optimized by AI."} color="indigo" />
                        <FeatureItem icon={Landmark} title={t('feat_datev') || 'DATEV Export'} description={t('feat_desc_datev') || "Complete data export for your tax advisor."} color="emerald" />
                    </div>
                </div>

                {/* SECURE LOGOS */}
                <div className="mt-24 pb-12 flex flex-wrap justify-center items-center gap-12 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-700">
                    <Lock size={32} />
                    <Landmark size={32} />
                    <ShieldCheck size={32} />
                    <DollarSign size={32} />
                </div>
            </main>
        </div>
    );
};

export default SubscriptionPage;